import React from 'react';
import { ScrollView, View, Linking } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { formatDistanceToNow } from 'date-fns';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text, Card, Button, AppBar } from '@ui';
import { Notification } from '@mocks/notifications';

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const HeaderCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const IconWrapper = styled.View<{ bgColor: string }>`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background-color: ${({ bgColor }) => bgColor};
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const MetaInfo = styled.View`
  flex-direction: row;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.md}px;
  padding-top: ${({ theme }) => theme.spacing.md}px;
  border-top-width: 1px;
  border-top-color: ${({ theme }) => theme.colors.divider};
`;

const MetaItem = styled.View`
  flex-direction: row;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.base}px;
`;

const BodyCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

type InboxDetailRouteProp = RouteProp<
  { InboxDetail: { notification: Notification } },
  'InboxDetail'
>;

const getIconName = (category: string): string => {
  switch (category) {
    case 'tasks':
      return 'task-alt';
    case 'benefits':
      return 'card-giftcard';
    case 'advisor':
      return 'person';
    default:
      return 'notifications';
  }
};

const getColorForCategory = (category: string, theme: any): string => {
  switch (category) {
    case 'tasks':
      return theme.colors.warningLight;
    case 'benefits':
      return theme.colors.successLight;
    case 'advisor':
      return theme.colors.infoLight;
    default:
      return theme.colors.neutral100;
  }
};

const getIconColorForCategory = (category: string, theme: any): string => {
  switch (category) {
    case 'tasks':
      return theme.colors.warning;
    case 'benefits':
      return theme.colors.success;
    case 'advisor':
      return theme.colors.info;
    default:
      return theme.colors.textSecondary;
  }
};

const InboxDetailScreen: React.FC = () => {
  const theme = useTheme();
  const route = useRoute<InboxDetailRouteProp>();
  const navigation = useNavigation();
  const { notification } = route.params;

  const handleAction = () => {
    if (notification.actionLink) {
      // In a real app, navigate to the action link
      Linking.openURL(`aia-member://${notification.actionLink}`);
    }
  };

  return (
    <Container edges={['top']}>
      <AppBar title="Notification" leftIcon="arrow-back" onLeftPress={() => navigation.goBack()} />
      <Content showsVerticalScrollIndicator={false}>
        <HeaderCard padding="lg">
          <IconWrapper bgColor={getColorForCategory(notification.category, theme)}>
            <Icon
              name={getIconName(notification.category)}
              size={28}
              color={getIconColorForCategory(notification.category, theme)}
            />
          </IconWrapper>

          <Text variant="headline" color="textPrimary" style={{ marginBottom: 8 }}>
            {notification.title}
          </Text>

          <MetaInfo>
            <MetaItem>
              <Icon name="schedule" size={16} color={theme.colors.textTertiary} />
              <Text variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
              </Text>
            </MetaItem>
            <MetaItem>
              <Icon name="label" size={16} color={theme.colors.textTertiary} />
              <Text variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                {notification.category.charAt(0).toUpperCase() + notification.category.slice(1)}
              </Text>
            </MetaItem>
          </MetaInfo>
        </HeaderCard>

        <BodyCard padding="lg">
          <Text variant="body" color="textPrimary" style={{ lineHeight: 24 }}>
            {notification.message}
          </Text>

          {notification.category === 'tasks' && (
            <View style={{ marginTop: 16 }}>
              <Text variant="bodyMedium" color="textSecondary">
                This is a friendly reminder to help you stay on track with your health journey.
                Taking timely action ensures you get the best care and outcomes.
              </Text>
            </View>
          )}

          {notification.category === 'benefits' && (
            <View style={{ marginTop: 16 }}>
              <Text variant="bodyMedium" color="textSecondary">
                Explore new benefits and programs designed specifically for your current life stage.
                These offerings can help you and your family thrive.
              </Text>
            </View>
          )}

          {notification.category === 'advisor' && (
            <View style={{ marginTop: 16 }}>
              <Text variant="bodyMedium" color="textSecondary">
                Your AIA advisor is here to help you make informed decisions about your coverage and
                financial wellbeing. Feel free to reach out anytime.
              </Text>
            </View>
          )}
        </BodyCard>

        {notification.actionLabel && (
          <Button variant="primary" fullWidth iconRight="arrow-forward" onPress={handleAction}>
            {notification.actionLabel}
          </Button>
        )}

        <Button
          variant="ghost"
          fullWidth
          onPress={() => navigation.goBack()}
          style={{ marginTop: 12 }}
        >
          Close
        </Button>

        <View style={{ height: 24 }} />
      </Content>
    </Container>
  );
};

export default InboxDetailScreen;
